/**
 * Shared display helpers for order customer contact + fulfillment type.
 * Backend stores a single delivery_address string (no short_address field).
 */

export const PRIVACY_PLACEHOLDER = "[Hidden for Privacy]";

export function isAgentFamilyOrder(order) {
  if (!order) return false;
  const platform = String(order.platform || "").toUpperCase();
  return (
    Boolean(order.is_chat_agent_order) ||
    platform === "CHAT_AGENT" ||
    platform === "DIGITAL_CATALOG"
  );
}

export function isCustomerInfoMasked(order) {
  if (!order) return false;
  if (order.customer_info_masked === true) return true;
  const values = [
    order.customer_name,
    order.customer_phone,
    order.delivery_phone,
    order.delivery_address,
  ];
  return values.some(
    (v) => typeof v === "string" && v.trim() === PRIVACY_PLACEHOLDER
  );
}

export function getPrivacyPlaceholderLabel(t) {
  if (typeof t === "function") {
    return t("ordersPage.privacy.hiddenPlaceholder", PRIVACY_PLACEHOLDER);
  }
  return PRIVACY_PLACEHOLDER;
}

/**
 * OneHub must not edit customer PII — updates come only from Order Agent & DC.
 * Kept as a shared helper so any leftover callers stay read-only.
 */
export function canEditOrderCustomerInfo() {
  return false;
}

export function getOrderContactPhone(order) {
  if (!order) return "";
  if (isCustomerInfoMasked(order)) return PRIVACY_PLACEHOLDER;
  const phone = (order.customer_phone || order.delivery_phone || "").trim();
  return phone;
}

export function isPickupOrder(order) {
  if (!order) return false;
  return String(order.userGetMode || "").toLowerCase().trim() === "pickup";
}

export function isDeliveryOrder(order) {
  return !isPickupOrder(order);
}

/**
 * Resolve address display value.
 * Pickup / empty → null (caller renders localized N/A).
 * Masked → privacy placeholder.
 * Otherwise returns trimmed full address for short truncation + hover/tap.
 */
export function getOrderAddressDisplay(order) {
  if (!order || isPickupOrder(order)) return null;
  if (isCustomerInfoMasked(order)) return PRIVACY_PLACEHOLDER;
  const address = (order.delivery_address || "").trim();
  return address || null;
}

export function getOrderTypeKey(order) {
  return isPickupOrder(order) ? "pickup" : "delivery";
}

export function isOrderAddressUpdated(order) {
  const fields = Array.isArray(order?.customer_info_updated_fields)
    ? order.customer_info_updated_fields
    : [];
  return Boolean(order?.customer_info_updated && fields.includes("delivery_address"));
}

export function isOrderCustomerDetailsUpdated(order) {
  if (!order?.customer_info_updated) return false;
  if (isOrderAddressUpdated(order)) {
    const fields = Array.isArray(order.customer_info_updated_fields)
      ? order.customer_info_updated_fields
      : [];
    // Other PII fields also changed → still show customer badge
    return fields.some((f) =>
      ["customer_name", "customer_phone", "delivery_phone", "delivery_instructions"].includes(f)
    );
  }
  return true;
}

export function getCustomerInfoUpdatedLabel(order, t) {
  if (isOrderAddressUpdated(order)) {
    return typeof t === "function"
      ? t("ordersPage.privacy.addressUpdatedBadge", "Address updated")
      : "Address updated";
  }
  return typeof t === "function"
    ? t("ordersPage.privacy.detailsUpdatedBadge", "Details updated")
    : "Details updated";
}
